/**
 * @fileoverview NFE.io SDK v3 - Official Node.js SDK for NFE.io API
 *
 * @description
 * Modern TypeScript SDK for NFE.io API with zero runtime dependencies.
 * Compatible with Node.js 18+ and modern JavaScript runtimes.
 *
 * @example Basic Usage
 * ```typescript
 * import { NfeClient } from 'nfe-io';
 *
 * const nfe = new NfeClient({
 *   apiKey: 'your-api-key',
 *   environment: 'production' // or 'sandbox'
 * });
 *
 * // Create a service invoice
 * const invoice = await nfe.serviceInvoices.create('company-id', {
 *   borrower: { /* ... *\/ },
 *   cityServiceCode: '12345',
 *   servicesAmount: 1000.00
 * });
 * ```
 *
 * @example With Polling
 * ```typescript
 * // Automatically poll until invoice is processed
 * const invoice = await nfe.serviceInvoices.createAndWait('company-id', data, {
 *   maxAttempts: 30,
 *   interval: 2000
 * });
 * ```
 *
 * @module nfe-io
 * @version 5.1.0
 * @author NFE.io
 * @license MIT
 */

// ============================================================================
// Main Exports
// ============================================================================

/**
 * Core client exports
 *
 * @see {@link NfeClient} - Main client class for NFE.io API
 * @see {@link createNfeClient} - Factory function for creating client instances
 */
export { NfeClient, createNfeClient, VERSION, SUPPORTED_NODE_VERSIONS, CTE_API_BASE_URL, LEGAL_ENTITY_API_BASE_URL, NATURAL_PERSON_API_BASE_URL } from './core/client.js';

/**
 * TypeScript type definitions for NFE.io API entities and configurations
 *
 * @see {@link NfeConfig} - Client configuration options
 * @see {@link Company} - Company entity type
 * @see {@link ServiceInvoice} - Service invoice entity type
 * @see {@link LegalPerson} - Legal person (empresa) entity type
 * @see {@link NaturalPerson} - Natural person (pessoa física) entity type
 * @see {@link AccountWebhook} - Webhook configuration type (account-scoped, `/v2/webhooks`)
 */
export type {
  // Configuration
  NfeConfig,
  RequiredNfeConfig,
  RetryConfig,

  // Entities
  Company,
  LegalPerson,
  NaturalPerson,
  ServiceInvoice,
  ServiceInvoiceData,
  ServiceInvoiceDetails,
  Webhook,
  WebhookEvent,
  AccountWebhook,
  WebhookEventType,

  // Address types
  Address,
  AddressCity,
  AddressLookupResponse,

  // CT-e (Transportation Invoice) types
  TransportationInvoiceInboundSettings,
  TransportationInvoiceMetadata,
  EnableTransportationInvoiceOptions,
  TransportationInvoiceEntityStatus,
  TransportationInvoiceMetadataType,

  // Inbound NF-e Distribution types
  InboundInvoiceMetadata,
  InboundProductInvoiceMetadata,
  InboundSettings,
  EnableInboundOptions,
  ManifestEventType,
  InboundCompany,
  InboundIssuer,
  InboundBuyer,
  InboundTransportation,
  InboundLinks,
  InboundProductInvoice,
  AutomaticManifesting,

  // Product Invoice Query types (consulta-nf)
  ProductInvoiceStatus,
  ProductInvoicePaymentType,
  ProductInvoiceOperationType,
  ProductInvoiceDestination,
  ProductInvoicePrintType,
  ProductInvoiceIssueType,
  ProductInvoiceEnvironmentType,
  ProductInvoicePurposeType,
  ProductInvoiceConsumerType,
  ProductInvoicePresenceType,
  ProductInvoiceProcessType,
  ProductInvoiceTaxRegimeCode,
  ProductInvoicePersonType,
  ProductInvoicePaymentMethod,
  ProductInvoiceCardFlag,
  ProductInvoiceIntegrationPaymentType,
  ProductInvoiceCity,
  ProductInvoiceAddress,
  ProductInvoiceIssuer,
  ProductInvoiceBuyer,
  ProductInvoiceIcmsTotals,
  ProductInvoiceIssqnTotals,
  ProductInvoiceTotals,
  ProductInvoiceItemIcms,
  ProductInvoiceItemTax,
  ProductInvoiceItem,
  ProductInvoiceTransport,
  ProductInvoicePayment,
  ProductInvoiceProtocol,
  ProductInvoiceAdditionalInfo,
  ProductInvoiceBilling,
  ProductInvoiceDetails,
  ProductInvoiceEvent,
  ProductInvoiceEventsResponse,

  // Consumer Invoice Query types (CFe-SAT / consulta-nf-consumidor)
  CouponStatus,
  CouponPersonType,
  CouponTaxRegime,
  CouponPaymentMethod,
  CouponIssqnTaxIncentive,
  CouponCity,
  CouponAddress,
  CouponIssuer,
  CouponBuyer,
  CouponIcmsTotal,
  CouponIssqnTotal,
  CouponTotal,
  CouponTaxBase,
  CouponIcmsTax,
  CouponPisTax,
  CouponCofinsTax,
  CouponIssqnTax,
  CouponItemTax,
  CouponFiscoField,
  CouponReferencedDocument,
  CouponItem,
  CouponPaymentDetail,
  CouponPayment,
  CouponDelivery,
  CouponAdditionalInformation,
  TaxCoupon,

  // Legal Entity Lookup types (consulta-cnpj)
  BrazilianState,
  LegalEntityBasicInfoOptions,
  LegalEntityBasicInfoResponse,
  LegalEntityStateTaxResponse,
  LegalEntityStateTaxForInvoiceResponse,
  LegalEntitySize,
  LegalEntityStatus,
  LegalEntityUnit,
  LegalEntityTaxRegime,
  LegalEntityNatureCode,
  LegalEntityStateTaxStatus,
  LegalEntityStateTaxForInvoiceStatus,
  LegalEntityFiscalDocumentStatus,
  LegalEntityActivityType,
  LegalEntityPhoneSource,
  LegalEntityCity,
  LegalEntityAddress,
  LegalEntityPhone,
  LegalEntityEconomicActivity,
  LegalEntityNature,
  LegalEntityQualification,
  LegalEntityPartner,
  LegalEntityFiscalDocumentInfo,
  LegalEntityStateTax,
  LegalEntityStateTaxForInvoice,
  LegalEntityBasicInfo,
  LegalEntityStateTaxInfo,
  LegalEntityStateTaxForInvoiceInfo,

  // Natural Person Lookup types (consulta-cpf)
  NaturalPersonStatus,
  NaturalPersonStatusResponse,

  // Tax Calculation types (calculo-impostos)
  TaxOperationType,
  TaxOrigin,
  TaxCalcTaxRegime,
  TaxIcms,
  TaxIcmsUfDest,
  TaxPis,
  TaxCofins,
  TaxIpi,
  TaxIi,
  CalculateRequestIssuer,
  CalculateRequestRecipient,
  CalculateItemRequest,
  CalculateRequest,
  CalculateItemResponse,
  CalculateResponse,
  TaxCode,
  TaxCodePaginatedResponse,
  TaxCodeListOptions,

  // NF-e Product Invoice types (nf-produto-v2)
  NfeEnvironmentType,
  NfeInvoiceStatus,
  NfeStateCode,
  NfeOperationType,
  NfePurposeType,
  NfePaymentMethod,
  NfeShippingModality,
  NfeConsumerPresenceType,
  NfePrintType,
  NfePersonType,
  NfeDestination,
  NfeConsumerType,
  NfePaymentType,
  NfeReceiverStateTaxIndicator,
  NfeFlagCard,
  NfeIntegrationPaymentType,
  NfeIntermediationType,
  NfeTaxRegime,
  NfeSpecialTaxRegime,
  NfeStateTaxProcessingAuthorizer,
  NfeFlowStatus,
  NfeAddress,
  NfeCity,
  NfeProductInvoiceBuyer,
  NfeCardResource,
  NfePaymentDetail,
  NfePaymentResource,
  NfeBillingResource,
  NfeBillingInvoice,
  NfeDuplicateResource,
  NfeIcmsTaxResource,
  NfeIpiTaxResource,
  NfePisTaxResource,
  NfeCofinsTaxResource,
  NfeIiTaxResource,
  NfeIcmsUfDestinationTaxResource,
  NfeInvoiceItemTax,
  NfeTaxDeterminationResource,
  NfeInvoiceItemResource,
  NfeTransportInformation,
  NfeTransportGroupResource,
  NfeVolumeResource,
  NfeAdditionalInformation,
  NfeExportResource,
  NfeIssuerFromRequest,
  NfeIntermediateResource,
  NfeDeliveryInformation,
  NfeWithdrawalInformation,
  NfeTotals,
  NfeTotalResource,
  NfeAuthorizationResource,
  NfeContingencyDetails,
  NfeActivityResource,
  NfeInvoiceEventsBase,
  NfeProductInvoiceIssueData,
  NfeIssuerResource,
  NfeProductInvoice,
  NfeProductInvoiceWithoutEvents,
  NfeProductInvoiceListOptions,
  NfeProductInvoiceListResponse,
  NfeInvoiceItemsResponse,
  NfeProductInvoiceEventsResponse,
  NfeProductInvoiceSubListOptions,
  NfeFileResource,
  InboundFileResource,
  ConsumerInvoiceItemsResponse,
  ConsumerInvoiceEventsResponse,
  ConsumerInvoiceCancellationResponse,
  ConsumerInvoiceFileResource,
  NfeRequestCancellationResource,
  NfeDisablementData,
  NfeDisablementResource,

  // State Tax (Inscrição Estadual) types
  NfeStateTaxType,
  NfeStateTaxEnvironmentType,
  NfeStateTaxStatus,
  NfeStateTaxStateCode,
  NfeStateTaxSpecialTaxRegime,
  NfeStateTax,
  NfeStateTaxCreateData,
  NfeStateTaxUpdateData,
  NfeStateTaxListResponse,
  NfeStateTaxListOptions,
  NfeSecurityCredential,

  // Common types
  EntityType,
  TaxRegime,
  SpecialTaxRegime,

  // HTTP and pagination
  HttpResponse,
  ListResponse,
  PageInfo,
  PaginationOptions,
  PollOptions,

  // Utility types
  ResourceId,
  ApiErrorResponse,

  // RTC (Reforma Tributária do Consumo) emission request types
  NFSeRtcRequest,
  ProductInvoiceRtcRequest,

  // Empresas (contribuintes-v2) spec-backed types
  CompanyResourceItem,
  CompanyResourceV1,
  CreateCompanyResourceItem,
  UpdateCompanyResourceItem,
  CompanyV2ListOptions,
  CompanyV2ListResponse,
  CertificateMetadataResource,
  CompanyAddress,
  MunicipalTax,
  CreateMunicipalTaxData,
  UpdateMunicipalTaxData,
  MunicipalTaxListResponse,
  ConsumerInvoiceData,
  ConsumerInvoice,
  ConsumerInvoiceListResponse,
  ConsumerInvoiceDisablementData,
  CertificatesMetadataResource,
  CertificateMetadataResourceItem,
  CertificateStatus,
  CompanyCertificateV1,
} from './core/types.js';

/**
 * Error classes and utilities for comprehensive error handling
 *
 * @see {@link NfeError} - Base error class for all SDK errors
 * @see {@link AuthenticationError} - Thrown when API key is invalid (401)
 * @see {@link ValidationError} - Thrown when request validation fails (400, 422)
 * @see {@link NotFoundError} - Thrown when resource not found (404)
 * @see {@link RateLimitError} - Thrown when rate limit exceeded (429)
 * @see {@link ServerError} - Thrown on server errors (500, 502, 503)
 * @see {@link ConnectionError} - Thrown on network/connection failures
 * @see {@link TimeoutError} - Thrown when request times out
 * @see {@link PollingTimeoutError} - Thrown when invoice polling times out
 */
export {
  // Base error
  NfeError,

  // HTTP errors
  AuthenticationError,
  ValidationError,
  NotFoundError,
  ConflictError,
  RateLimitError,
  ServerError,

  // Connection errors
  ConnectionError,
  TimeoutError,

  // SDK errors
  ConfigurationError,
  PollingTimeoutError,
  InvoiceProcessingError,

  // Error factory
  ErrorFactory,

  // Type guards
  isNfeError,
  isAuthenticationError,
  isValidationError,
  isNotFoundError,
  isConnectionError,
  isTimeoutError,
  isPollingTimeoutError,

  // Legacy aliases (v2 compatibility)
  BadRequestError,
  APIError,
  InternalServerError,

  // Error types
  ErrorTypes,
  type ErrorType,
} from './core/errors/index.js';

// ============================================================================
// Certificate Validator
// ============================================================================

/**
 * Certificate validation utilities
 *
 * @see {@link CertificateValidator} - Certificate validation utility class
 *
 * @example
 * ```typescript
 * import { CertificateValidator } from 'nfe-io';
 *
 * const validation = await CertificateValidator.validate(certBuffer, 'password');
 * if (validation.valid) {
 *   console.log('Certificate expires:', validation.metadata?.validTo);
 * }
 * ```
 */
export { CertificateValidator } from './core/utils/certificate-validator.js';

// ============================================================================
// Resource Classes (for advanced usage)
// ============================================================================

/**
 * Transportation Invoices (CT-e) Resource
 *
 * @see {@link TransportationInvoicesResource} - CT-e operations via Distribuição DFe
 *
 * @example
 * ```typescript
 * import { TransportationInvoicesResource } from 'nfe-io';
 *
 * // For advanced usage when extending the SDK
 * class CustomCteResource extends TransportationInvoicesResource {
 *   // Add custom methods
 * }
 * ```
 */
export { TransportationInvoicesResource } from './core/resources/transportation-invoices.js';
export { TaxCalculationResource, createTaxCalculationResource } from './core/resources/tax-calculation.js';
export { TaxCodesResource, createTaxCodesResource } from './core/resources/tax-codes.js';
export { ProductInvoicesResource } from './core/resources/product-invoices.js';
export { StateTaxesResource } from './core/resources/state-taxes.js';
export { ServiceInvoicesRtcResource } from './core/resources/service-invoices-rtc.js';
export { ProductInvoicesRtcResource } from './core/resources/product-invoices-rtc.js';
export { MunicipalTaxesResource } from './core/resources/municipal-taxes.js';
export { ConsumerInvoicesResource } from './core/resources/consumer-invoices.js';
export type {
  ConsumerInvoiceListOptions,
  ConsumerInvoiceEnvironment,
  // Parâmetro de `getItems`/`getEvents`: aparecia na assinatura pública sem ser
  // importável, então o chamador não conseguia nomear o próprio argumento.
  ConsumerInvoicePageOptions,
} from './core/resources/consumer-invoices.js';

// Retorno de `companies.getCertificateStatus()`. Mesmo motivo: estava na
// assinatura pública e fora da lista de exports.
export type { CertificateStatusSummary } from './core/resources/companies.js';
export { CertificatesResource } from './core/resources/certificates.js';
export { NotificationsResource } from './core/resources/notifications.js';
export type { Notification, NotificationListResponse } from './core/resources/notifications.js';
export type {
  CreateInvoiceResponse,
  CancelInvoiceResponse,
} from './core/resources/service-invoices.js';

// ============================================================================
// Default Export (maintains v2 compatibility)
// ============================================================================

/**
 * Default export for CommonJS compatibility
 *
 * @description
 * Allows both ES modules and CommonJS usage:
 *
 * @example ES Modules
 * ```typescript
 * import { NfeClient } from 'nfe-io';
 * const nfe = new NfeClient({ apiKey: 'xxx' });
 * ```
 *
 * @example ES Modules (default import)
 * ```typescript
 * import nfeFactory from 'nfe-io';
 * const nfe = nfeFactory({ apiKey: 'xxx' });
 * ```
 *
 * @example CommonJS
 * ```javascript
 * const { NfeClient } = require('nfe-io');
 * const nfe = new NfeClient({ apiKey: 'xxx' });
 * ```
 *
 * @example CommonJS (default require)
 * ```javascript
 * const nfeFactory = require('nfe-io').default;
 * const nfe = nfeFactory({ apiKey: 'xxx' });
 * ```
 */
import nfeFactory from './core/client.js';
import { PACKAGE_NAME as PKG_NAME, VERSION as PKG_VERSION } from './version.js';
export default nfeFactory;

// ============================================================================
// Package Information
// ============================================================================

/**
 * NPM package name
 *
 * Vem de `src/version.ts`, gerado do `package.json`. Até 2026-09-02 esta constante
 * dizia `@nfe-io/sdk` — pacote que não existe; o publicado é `nfe-io`.
 *
 * @constant
 */
export const PACKAGE_NAME = PKG_NAME;

/**
 * Current SDK version
 *
 * Vem da mesma fonte. NÃO fixar literal: até 2026-09-02 esta constante dizia
 * `5.1.0`, `VERSION` dizia o mesmo, o `package.json` dizia `5.2.0` e o User-Agent
 * dizia `3.0.0` — quatro valores para uma informação só.
 *
 * @constant
 */
export const PACKAGE_VERSION = PKG_VERSION;

/**
 * NFE.io API version supported by this SDK
 * @constant
 */
export const API_VERSION = 'v1';

/**
 * GitHub repository URL
 * @constant
 */
export const REPOSITORY_URL = 'https://github.com/nfe/client-nodejs';

/**
 * Official NFE.io API documentation URL
 * @constant
 */
export const DOCUMENTATION_URL = 'https://nfe.io/docs';

// ============================================================================
// Environment Detection & Utilities
// ============================================================================

/**
 * Check if the current environment supports NFE.io SDK v3 requirements
 *
 * @description
 * Validates that the runtime environment has all necessary features:
 * - Node.js 18+ (for native fetch support)
 * - Fetch API availability
 * - AbortController availability
 *
 * @returns Object containing support status and detected issues
 *
 * @example
 * ```typescript
 * const check = isEnvironmentSupported();
 * if (!check.supported) {
 *   console.error('Environment issues:', check.issues);
 *   console.error('Node version:', check.nodeVersion);
 * }
 * ```
 */
export function isEnvironmentSupported(): {
  /** Whether all requirements are met */
  supported: boolean;
  /** Detected Node.js version (e.g., "v18.17.0") */
  nodeVersion?: string;
  /** Whether Fetch API is available */
  hasFetch: boolean;
  /** Whether AbortController is available */
  hasAbortController: boolean;
  /** List of detected compatibility issues */
  issues: string[];
} {
  const issues: string[] = [];
  let nodeVersion: string | undefined;

  // Check Node.js version
  try {
    nodeVersion = (globalThis as any).process?.version;
    if (nodeVersion) {
      const majorVersion = parseInt(nodeVersion.slice(1).split('.')[0]!);
      if (majorVersion < 18) {
        issues.push(`Node.js ${majorVersion} is not supported. Requires Node.js 18+.`);
      }
    }
  } catch {
    issues.push('Unable to detect Node.js version');
  }

  // Check fetch support
  const hasFetch = typeof fetch !== 'undefined';
  if (!hasFetch) {
    issues.push('Fetch API not available');
  }

  // Check AbortController support
  const hasAbortController = typeof AbortController !== 'undefined';
  if (!hasAbortController) {
    issues.push('AbortController not available');
  }

  const result: {
    supported: boolean;
    nodeVersion?: string;
    hasFetch: boolean;
    hasAbortController: boolean;
    issues: string[];
  } = {
    supported: issues.length === 0,
    hasFetch,
    hasAbortController,
    issues,
  };

  if (nodeVersion) {
    result.nodeVersion = nodeVersion;
  }

  return result;
}

/**
 * Get comprehensive SDK runtime information
 *
 * @description
 * Returns detailed information about the current runtime environment,
 * useful for debugging and support.
 *
 * @returns Object containing SDK and runtime environment information
 *
 * @example
 * ```typescript
 * const info = getRuntimeInfo();
 * console.log('SDK Version:', info.sdkVersion);
 * console.log('Node Version:', info.nodeVersion);
 * console.log('Platform:', info.platform);
 * console.log('Environment:', info.environment);
 * ```
 */
export function getRuntimeInfo(): {
  /** Current SDK version */
  sdkVersion: string;
  /** Node.js version (e.g., "v18.17.0") */
  nodeVersion: string;
  /** Operating system platform (e.g., "linux", "darwin", "win32") */
  platform: string;
  /** CPU architecture (e.g., "x64", "arm64") */
  arch: string;
  /** Runtime environment type */
  environment: 'node' | 'browser' | 'unknown';
} {
  let nodeVersion = 'unknown';
  let platform = 'unknown';
  let arch = 'unknown';
  let environment: 'node' | 'browser' | 'unknown' = 'unknown';

  try {
    const process = (globalThis as any).process;
    if (process) {
      nodeVersion = process.version || 'unknown';
      platform = process.platform || 'unknown';
      arch = process.arch || 'unknown';
      environment = 'node';
    } else if (typeof window !== 'undefined' && typeof (window as any).navigator !== 'undefined') {
      environment = 'browser';
      platform = (window as any).navigator.platform || 'unknown';
    }
  } catch {
    // Safe fallback
  }

  return {
    sdkVersion: PACKAGE_VERSION,
    nodeVersion,
    platform,
    arch,
    environment,
  };
}

// ============================================================================
// Quick Start Helpers
// ============================================================================

/**
 * Create NFE.io client from environment variable
 *
 * @description
 * Convenience function that reads API key from NFE_API_KEY environment variable.
 * Useful for serverless functions and quick prototyping.
 *
 * @param environment - Target environment ('production' or 'sandbox')
 * @returns Configured NfeClient instance
 * @throws {ConfigurationError} If NFE_API_KEY environment variable is not set
 *
 * @example
 * ```typescript
 * // Set environment variable: NFE_API_KEY=your-api-key
 * const nfe = createClientFromEnv('production');
 *
 * // Use the client normally
 * const companies = await nfe.companies.list();
 * ```
 *
 * @example Docker/Kubernetes
 * ```yaml
 * env:
 *   - name: NFE_API_KEY
 *     valueFrom:
 *       secretKeyRef:
 *         name: nfe-credentials
 *         key: api-key
 * ```
 */
export function createClientFromEnv(environment?: 'production' | 'sandbox') {
  const apiKey = (globalThis as any).process?.env?.NFE_API_KEY;
  if (!apiKey) {
    const { ConfigurationError } = require('./core/errors');
    throw new ConfigurationError(
      'NFE_API_KEY environment variable is required when using createClientFromEnv()'
    );
  }

  const { NfeClient } = require('./core/client');
  return new NfeClient({
    apiKey,
    environment: environment || 'production'
  });
}

/**
 * Validate NFE.io API key format
 *
 * @description
 * Performs basic validation on API key format before attempting to use it.
 * Helps catch common mistakes like missing keys or keys with whitespace.
 *
 * @param apiKey - The API key to validate
 * @returns Validation result with any detected issues
 *
 * @example
 * ```typescript
 * const result = validateApiKeyFormat('my-api-key');
 * if (!result.valid) {
 *   console.error('API key issues:', result.issues);
 *   // ["API key appears to be too short"]
 * }
 * ```
 *
 * @example Integration with client
 * ```typescript
 * const apiKey = process.env.NFE_API_KEY;
 * const validation = validateApiKeyFormat(apiKey);
 *
 * if (!validation.valid) {
 *   throw new Error(`Invalid API key: ${validation.issues.join(', ')}`);
 * }
 *
 * const nfe = new NfeClient({ apiKey });
 * ```
 */
export function validateApiKeyFormat(apiKey: string): {
  /** Whether the API key passes basic validation */
  valid: boolean;
  /** List of validation issues found */
  issues: string[];
} {
  const issues: string[] = [];

  if (!apiKey) {
    issues.push('API key is required');
  } else {
    if (apiKey.length < 10) {
      issues.push('API key appears to be too short');
    }

    if (apiKey.includes(' ')) {
      issues.push('API key should not contain spaces');
    }

    // Add more validation rules as needed
  }

  return {
    valid: issues.length === 0,
    issues,
  };
}
